import React, { useContext, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Style from "./style.module.css";
import moment from "moment";
import { Trash2 } from "lucide-react";
import Select from "react-select";
import { useApiCall } from "../../custom-hooks/api-call";
import { GlobalContext } from "../../../context/Context";
import Tooltip from "../../ui/tooltip";
import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
  FormLineBreak,
  SelectSearch,
} from "../../form/form-layout";
import LeftSidePopupLayout from "../../popup/modal-layout";
import XCloseIcon from "../../icons/x-close";

const stripHtml = (html) => (html || "").replace(/<[^>]*>?/gm, "");

const htmlToText = (html = "") => {
  return html
    .replace(/\t/g, "") // remove tabs
    .replace(/\r/g, "") // remove carriage returns
    .replace(/<\/li>/gi, "\n") // li end → newline
    .replace(/<br\s*\/?>/gi, "\n") // br → newline
    .replace(/<[^>]*>/g, "") // remove all other HTML tags
    .replace(/&amp;/g, "&") // decode &amp;
    .replace(/\n\s*\n+/g, "\n") // remove multiple empty lines
    .trim(); // trim start/end
};

const ConvertLeadToDeal = ({
  onSubmit = () => { },
  onClose = () => { },
  onError = () => { },
  leadData,
  bulkData = [],
  DealStages = [],
}) => {
  let { state, dispatch } = useContext(GlobalContext);

  const [mainProducts, setMainProducts] = useState([]);
  const [subProducts, setSubProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingSub, setLoadingSub] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const metas = Object.fromEntries(
    leadData?.metas?.map((m) => [m.meta_key, m.meta_value]) ?? [],
  );
  const initialMainId = metas?.main_product ? Number(metas.main_product) : null;
  const initialSubId = leadData?.deal_product
    ? Number(leadData.deal_product)
    : null;
  const initialPackageId = leadData?.deal_package
    ? Number(metas.deal_package)
    : null;

  const apiCall = useApiCall();

  useEffect(() => {
    formik.setFieldValue("dealstage", DealStages[0]?.name);
    if (leadData?.leadid || leadData?.id) {
      formik.setFieldValue("name", leadData?.name || "");
    }
  }, []);

  const [packageFeatures, setPackageFeatures] = useState("");

  const AddField = () => {
    const newFields = [
      ...formik.values.extra_meta,
      { meta_key: "", meta_value: "" },
    ];
    formik.setFieldValue("extra_meta", newFields);
  };

  const DeleteField = (index) => {
    const newFields = formik.values.extra_meta.filter((_, i) => i !== index);
    formik.setFieldValue("extra_meta", newFields);
  };

  const validationSchema = yup.object({
    name: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    main_product: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    deal_product: yup.string().when("main_product", {
      is: (val) => Number(val) !== 9,
      then: (schema) => schema.required("This Field is Required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    deal_package: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    estimated_payment_date: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    dealamount: yup
      .number("This Field is Required")
      .required("This Field is Required"),
    dealprobability: yup
      .number("This Field is Required")
      .required("This Field is Required"),
    description: yup
      .string("This Field is Required")
      .required("This Field is Required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      deal_product: "",
      deal_package: "",
      features: "",
      main_product: initialMainId || "",
      estimated_payment_date: "",
      dealamount: "",
      dealstage: "",
      dealprobability: "",
      description: "",
      extra_meta: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      let extra_meta = values.extra_meta.map((item) => ({
        key: item.meta_key,
        value: item.meta_value,
      }));

      if (packageFeatures) {
        extra_meta.push({
          key: "features",
          value: packageFeatures || "",
        });
      }
      extra_meta.push({
        key: "main_product",
        value: values.main_product,
      });

      let data = {
        // ...extraData,
        name: values.name,
        deal_product:
          values.main_product !== 9 ? Number(values.deal_product) : 9,
        deal_package: Number(values.deal_package),
        estimated_payment_date: values.estimated_payment_date,
        dealamount: Number(values.dealamount),
        dealstage: values.dealstage,
        dealprobability: values.dealprobability,
        description: values.description,
        extra_meta: extra_meta,
      };

      // console.log("Form data", data);
      // console.log("Extra meta", extra_meta);


      try {
        let apiRes;
        if (leadData?.leadid || leadData?.id) {
          // single convert
          apiRes = await apiCall({
            method: "post",
            url: `leads/convert-lead/${leadData.leadid || leadData?.id}`,
            data: data,
          });
        } else if (bulkData?.length > 0) {
          // bulk convert
          apiRes = await apiCall({
            method: "post",
            url: `leads/convert-bulk-lead`,
            data: {
              lead_ids: bulkData.map((item) => item.id),
              ...data,
            },
          });
        }
        formik.resetForm();
        onSubmit(apiRes?.message || `Request Submitted`);
      } catch (error) {
        console.log("Error", error);
        const res = error?.response?.data;

        if (res?.errors) {
          formik.setErrors(
            Object.fromEntries(
              Object.entries(res.errors).map(([key, value]) => [key, value[0]]),
            ),
          );
        }
        onError(
          error?.response.data?.respMsg ||
          error?.response.data?.responseMessage ||
          error?.response.data?.message ||
          "Some Thing Went Wrong",
        );
      }
    },
  });

  const [TotalStages, setTotalStages] = useState(5);
  const [CurrentStage, setCurrentStage] = useState(1);

  useEffect(() => {
    let totalStages = DealStages?.length;
    setTotalStages(totalStages);
  }, []);

  const handleStageClick = (StageId) => {
    setCurrentStage(StageId);

    const selectedValue = DealStages.find((stage) => stage.id == StageId)?.name;
    formik.setFieldValue("dealstage", selectedValue || "");
  };

  useEffect(() => {
    loadMainProducts();
  }, []);

  useEffect(() => {
    if (initialMainId) {
      loadSubProducts(initialMainId).then(() => {
        if (initialSubId) {
          loadPackages(initialSubId);
        }
      });
    } else if (initialSubId) {
      loadPackages(initialSubId);
    }
  }, [initialMainId, initialSubId]);

  const loadMainProducts = async () => {
    setLoadingMain(true);
    try {
      const res = await apiCall({
        method: "get",
        url: "products/products-list?is_paginate=0",
      });
      setMainProducts(res?.data || []);
    } catch (err) {
      console.warn("Error loading main products:", err);
    } finally {
      setLoadingMain(false);
    }
  };

  const loadSubProducts = async (mainId) => {
    if (!mainId) {
      setSubProducts([]);
      return;
    }
    setLoadingSub(true);
    try {
      const res = await apiCall({
        method: "get",
        url: `products/get-sub-products/${mainId}`,
      });
      setSubProducts(res?.data || []);
    } catch (err) {
      console.warn("Error loading sub-products:", err);
      setSubProducts([]);
    } finally {
      setLoadingSub(false);
    }
  };

  const loadPackages = async (subProductId) => {
    if (!subProductId) {
      setPackages([]);
      return;
    }
    setLoadingPackages(true);
    try {
      const res = await apiCall({
        method: "get",
        url: `products/get-sub-product-packages/${subProductId}`,
      });
      setPackages(res?.data || []);
    } catch (err) {
      console.warn("Error loading packages:", err);
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  // Handlers for selects
  const handleMainSelect = async (option) => {
    const id = option || "";

    formik.setFieldValue("main_product", id);

    formik.setFieldValue("deal_product", "");
    formik.setFieldValue("deal_package", "");
    formik.setFieldValue("features", "");
    setSubProducts([]);
    setPackages([]);

    if (id) {
      id == 9 ? await loadPackages(id) : await loadSubProducts(id);
    }
  };

  const handleSubSelect = async (option) => {
    const id = option || "";
    formik.setFieldValue("deal_product", id);

    formik.setFieldValue("deal_package", "");
    formik.setFieldValue("features", "");
    setPackages([]);

    if (id) {
      await loadPackages(id);
    }
  };

  const handlePackageSelect = (option) => {
    const id = option || "";
    const pkg = packages.find((p) => Number(p.packageid) === Number(id));
    // formik.setFieldValue("deal_package_id", id);
    formik.setFieldValue("deal_package", id || "");

    // set features from package if exists
    if (pkg?.features) {
      formik.setFieldValue("features", htmlToText(pkg.features || ""));
      setPackageFeatures(pkg?.features || "");
    } else {
      formik.setFieldValue("features", "");
      setPackageFeatures("");
    }
  };

  const subProductsUnique = subProducts.filter(
    (v, i, a) =>
      a.findIndex(
        (t) =>
          Number(t.id ?? t.subproductid ?? t.productid) ===
          Number(v.id ?? v.subproductid ?? v.productid),
      ) === i,
  );
  const packagesUnique = packages.filter(
    (v, i, a) =>
      a.findIndex((t) => Number(t.packageid) === Number(v.packageid)) === i,
  );

  const mainOptions = mainProducts.map((mp) => ({
    value: mp.id ?? mp.productid ?? mp.main_product_id ?? mp.id,
    label: mp.products || mp.name || mp.title || `#${mp.id}`,
  }));
  const subOptions = subProductsUnique.map((sp) => ({
    value: sp.id ?? sp.subproductid ?? sp.productid,
    label: sp.products || sp.name || sp.title || `#${sp.id}`,
  }));
  const packageOptions = [
    ...packagesUnique.map((pk) => ({
      value: pk.packageid,
      label: pk.packagename || `#${pk.packageid}`,
    })),
    { value: 3, label: "Other" },
  ];

  const editMainId = metas?.main_product ? Number(metas.main_product) : null;
  const editSubId = leadData?.deal_product
    ? Number(leadData.deal_product)
    : null;
  const editPackageId = leadData?.deal_package
    ? Number(leadData.deal_package)
    : null;

  useEffect(() => {
    let mounted = true;
    const runPrefill = async () => {
      try {
        if (editMainId) {
          formik.setFieldValue("main_product", editMainId, false);

          await loadSubProducts(editMainId);
        }

        if (editSubId) {
          const existsInSub = subProducts.some(
            (sp) =>
              Number(sp.id ?? sp.subproductid ?? sp.productid) ===
              Number(editSubId),
          );
          if (!existsInSub) {
            setSubProducts((prev) => {
              const merged = [
                ...prev,
                {
                  id: editSubId,
                  products:
                    leadData?.deal_product_name || `Product #${editSubId}`,
                },
              ];

              const unique = merged.filter(
                (v, i, a) =>
                  a.findIndex(
                    (t) =>
                      Number(t.id ?? t.subproductid ?? t.productid) ===
                      Number(v.id ?? v.subproductid ?? v.productid),
                  ) === i,
              );
              return unique;
            });
          }

          formik.setFieldValue("deal_product", editSubId, false);

          await loadPackages(editSubId);

          if (editPackageId) {
            const existsPkg = packages.some(
              (pk) => Number(pk.packageid) === Number(editPackageId),
            );
            if (!existsPkg) {
              setPackages((prev) => {
                const merged = [
                  ...prev,
                  {
                    packageid: editPackageId,
                    packagename: metas?.packagename,
                  },
                ];

                const unique = merged.filter(
                  (v, i, a) =>
                    a.findIndex(
                      (t) => Number(t.packageid) === Number(v.packageid),
                    ) === i,
                );
                return unique;
              });
            }

            formik.setFieldValue("deal_package_id", editPackageId, false);

            formik.setFieldValue(
              "deal_package",
              leadData?.deal_package || metas?.deal_package_name || "",
              false,
            );
          }
        } else if (!editSubId && editPackageId) {
          const existsPkg = packages.some(
            (pk) => Number(pk.packageid) === Number(editPackageId),
          );
          if (!existsPkg) {
            setPackages((prev) => {
              const merged = [
                ...prev,
                {
                  packageid: editPackageId,
                  packagename: metas?.packagename,
                },
              ];

              const unique = merged.filter(
                (v, i, a) =>
                  a.findIndex(
                    (t) => Number(t.packageid) === Number(v.packageid),
                  ) === i,
              );
              return unique;
            });
          }
          formik.setFieldValue("deal_package_id", editPackageId, false);
        }
      } catch (err) { }
    };

    if (leadData) {
      runPrefill();
    }

    return () => {
      mounted = false;
    };
  }, [leadData]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        <div
          className={`${Style["modal_header"]} ${Style.addMeModal_head} !flex !justify-between !items-center`}
        >
          <h4>
            {leadData ? "Convert to Deal" : "Bulk Convert to Deals"}
          </h4>
          {/* <div
            onClick={onClose}
            className={`${Style["close_btn"]} cursor-pointer hover:text-red-500 p-1 rounded hover:bg-gray-200 transition-all duration-300`}
          >
            <XCloseIcon />
          </div> */}
        </div>

        <div className={`${Style.addMeModal_body}`}>
          <div className={`${Style.dcmb__card}`}>
            <div className={`${Style.dcmb__cardBody}`}>
              <div className={`${Style.dcmb__cardBody_formArea} pt-3`}>
                <FormArea>
                  {/* Name */}
                  <FeildWraper>
                    <FormLabel required>Name</FormLabel>
                    <input
                      type="text"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="name"
                      disabled={leadData?.leadid || leadData?.id}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <FeildError>{formik.errors.name}</FeildError>
                    )}
                  </FeildWraper>

                  {/* Date */}
                  <FeildWraper>
                    <FormLabel required>Estimated Date</FormLabel>
                    <input
                      type="date"
                      name="estimated_payment_date"
                      min={new Date().toISOString().split("T")[0]}
                      value={formik.values.estimated_payment_date}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      placeholder="Payment Date"
                    />
                    {formik.touched.estimated_payment_date &&
                      formik.errors.estimated_payment_date && (
                        <FeildError>
                          {formik.errors.estimated_payment_date}
                        </FeildError>
                      )}
                  </FeildWraper>

                  {/* Pipeline stage */}
                  <FeildWraper className="!max-w-md">
                    <FormLabel>Pipeline stage</FormLabel>
                    <div className="flex  overflow-hidden h-8">
                      {DealStages?.map((stage, index) => {
                        // const isActive = index === CurrentStage;
                        const isFirst = index === 0;
                        const isLast = index === TotalStages - 1;

                        //  Set clip-path based on position
                        let clipPath = "";
                        if (isFirst) {
                          clipPath = `
                                        polygon(
                                          0 0,
                                          calc(100% - 10px) 0,
                                          100% 50%,
                                          calc(100% - 10px) 100%,
                                          0 100%
                                        )
                                          
                                      `;
                        } else if (isLast) {
                          clipPath = `
                                      polygon(
                                        0 0,
                                        10px 50%,
                                        0 100%,
                                        100% 100%,
                                        100% 0
                                      )
                                    `;
                        } else {
                          clipPath = `
                                        polygon(
                                          0 0,
                                          10px 50%,
                                          0 100%,
                                          calc(100% - 10px) 100%,
                                          100% 50%,
                                          calc(100% - 10px) 0
                                        )
                                      `;
                        }

                        return (
                          <Tooltip
                            key={`${index}-asideBottomItems`}
                            className={`${Style.Tooltip}`}
                            boxClassName={` ${Style.TooltipBox}  !z-[99999999]`}
                            content={stage?.name}
                            position={"bottom center"}
                          >
                            <div
                              key={index}
                              onClick={() => {
                                handleStageClick(stage.id);
                              }}
                              className={`
                                              min-h-[27px] min-w-[60px]  flex-1 
                                            ${stage.id <= CurrentStage
                                  ? "bg-[#2563eb]"
                                  : "bg-gray-200"
                                }
                                            ${isFirst
                                  ? `rounded-tl-md rounded-bl-md`
                                  : isLast
                                    ? "rounded-tr-md rounded-br-md"
                                    : null
                                }
                                          `}
                              style={{
                                clipPath: clipPath,
                                WebkitClipPath: clipPath,
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </div>
                  </FeildWraper>

                  {/* Amount */}
                  <FeildWraper>
                    <FormLabel required>Amount</FormLabel>
                    {/* <input
                      type="number"
                      name="dealamount"
                      min="0"
                      value={formik.values.dealamount}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      placeholder="Amount"
                    /> */}
                    <input
                      type="text"
                      name="dealamount"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={formik.values.dealamount}
                      onBlur={formik.handleBlur}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        formik.setFieldValue("dealamount", value);
                      }}
                      placeholder="Max Points"
                    />
                    {formik.touched.dealamount && formik.errors.dealamount && (
                      <FeildError>{formik.errors.dealamount}</FeildError>
                    )}
                  </FeildWraper>

                  {/* description */}
                  <FeildWraper>
                    <FormLabel required>Description</FormLabel>
                    <input
                      type="text"
                      name="description"
                      value={formik.values.description}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      placeholder="Description"
                      autoComplete="off"
                    />

                    {formik.touched.description &&
                      formik.errors.description && (
                        <FeildError>{formik.errors.description}</FeildError>
                      )}
                  </FeildWraper>

                  {/* Probability */}
                  <FeildWraper>
                    <FormLabel required>Probability</FormLabel>

                    <SelectSearch
                      name="dealprobability"
                      placeholder="- Select Probability -"
                      onChange={(val) => {
                        formik.setFieldValue("dealprobability", val);
                      }}
                      value={formik.values.dealprobability}
                      onBlur={() =>
                        formik.setFieldTouched("dealprobability", true, true)
                      }
                      options={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(
                        (ele) => ({
                          label: ele,
                          value: ele,
                        }),
                      )}
                    />
                    {formik.touched.dealprobability &&
                      formik.errors.dealprobability && (
                        <FeildError>{formik.errors.dealprobability}</FeildError>
                      )}
                  </FeildWraper>

                  {/* Main Product */}

                  <FeildWraper>
                    <FormLabel required>Main Product</FormLabel>

                    <SelectSearch
                      name="main_product"
                      placeholder="- Select Product -"
                      onChange={handleMainSelect}
                      value={formik?.values.main_product}
                      options={mainOptions}
                      onBlur={() =>
                        formik.setFieldTouched("main_product", true, true)
                      }
                    // menuPosition="fixed"
                    />
                    {formik.touched.main_product &&
                      formik.errors.main_product && (
                        <FeildError>{formik.errors.main_product}</FeildError>
                      )}
                  </FeildWraper>

                  {/* Sub product */}
                  {formik.values.main_product !== 9 ? (
                    <FeildWraper>
                      <FormLabel required>Sub Product</FormLabel>

                      <SelectSearch
                        name="deal_product"
                        placeholder={
                          formik.values.main_product
                            ? "- Select Sub Product -"
                            : "Please select a product first"
                        }
                        onChange={(val) => {
                          formik.setFieldValue("deal_product", val ? val : "");
                          handleSubSelect(val);
                        }}
                        isDisabled={!formik.values.main_product}
                        value={formik?.values.deal_product}
                        options={subOptions}
                        onBlur={() =>
                          formik.setFieldTouched("deal_product", true, true)
                        }
                      // menuPosition="fixed"
                      />
                      {formik.touched.deal_product &&
                        formik.errors.deal_product && (
                          <FeildError>{formik.errors.deal_product}</FeildError>
                        )}
                    </FeildWraper>
                  ) : null}

                  {/* Deal Package */}

                  <FeildWraper
                    width={formik.values.main_product !== 9 ? `100%` : `50%`}
                  >
                    <FormLabel>Package</FormLabel>

                    <SelectSearch
                      name="deal_package"
                      placeholder={
                        formik.values.deal_product ||
                          formik.values.main_product == 9
                          ? "- Select Package -"
                          : "Please select a sub product first"
                      }
                      onChange={handlePackageSelect}
                      isDisabled={
                        !formik.values.deal_product &&
                        formik.values.main_product !== 9
                      }
                      value={formik.values.deal_package}
                      options={
                        formik.values.deal_product ||
                          formik.values.main_product == 9
                          ? packageOptions
                          : []
                      }
                      onBlur={() =>
                        formik.setFieldTouched("deal_package", true, true)
                      }
                    // menuPosition="fixed"
                    />
                    {formik.touched.deal_package &&
                      formik.errors.deal_package && (
                        <FeildError>{formik.errors.deal_package}</FeildError>
                      )}
                  </FeildWraper>

                  {/* Features */}
                  <FeildWraper width="100%">
                    <FormLabel>Features</FormLabel>
                    <textarea
                      name="features"
                      value={formik.values.features}
                      onChange={formik.handleChange}
                      rows={6}
                      placeholder="Write or edit package features here..."
                      className="max-h-96 min-h-40"
                    />
                  </FeildWraper>

                  <FormLineBreak />

                  {Array.isArray(formik.values.extra_meta) &&
                    formik.values.extra_meta.length > 0 && (
                      <div className="flex  items-center py-2  ">
                        <h1 className="text-md font-medium  px-3 py-1 ">
                          Additional Fields
                        </h1>
                      </div>
                    )}

                  <FeildWraper width="100%">
                    {formik?.values?.extra_meta.map((field, index) => (
                      <div className={`w-full flex gap-3`} key={index}>
                        {/* key */}

                        <input
                          type="text"
                          name={`extra_meta[${index}].meta_key`}
                          value={formik.values.extra_meta[index].meta_key}
                          onChange={formik.handleChange}
                          placeholder="key"
                        />

                        {/* value */}

                        <input
                          type="text"
                          name={`extra_meta[${index}].meta_value`}
                          value={formik.values.extra_meta[index].meta_value}
                          onChange={formik.handleChange}
                          placeholder="value"
                        />

                        <Tooltip
                          key={`${index}-asideBottomItems`}
                          className={`${Style.Tooltip}`}
                          boxClassName={Style.TooltipBox}
                          content="Delete Field"
                          position={"bottom center"}
                        >
                          <div
                            onClick={() => DeleteField(index)}
                            className="p-3 text-red-600"
                          >
                            <Trash2 size={18} />
                          </div>
                        </Tooltip>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={AddField}
                      className="flex flex-start text-blue-700 font-medium   w-full p-2 text-[16px]"
                    >
                      + Add Field
                    </button>
                  </FeildWraper>
                </FormArea>
              </div>
            </div>
          </div>
        </div>

        <div className={`${Style.dcmb__cardFoot}`}>
          <div className="flex gap-3 w-[50%]">

            <button
              type="reset"
              className={`${Style.dcmb__cancel}`}
              onClick={onClose}
            >
              Discard
            </button>
            <button type="submit" className={Style.dcmb__submit}>
              {bulkData && bulkData.length > 0
                ? `Apply (${bulkData.length} ${bulkData.length === 1 ? "Lead" : "Leads"
                })`
                : "Apply"}
            </button>
          </div>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default ConvertLeadToDeal;
